import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import ImageUploadCrop from './components/ImageUploadCrop';
import { API_BASE_URL } from './utils/apiConfig';
import './styles-player-register.css';

function PlayerRegister({ tournamentCode: propTournamentCode, adminMode = false, onSuccess }) {
  const { tournamentCode: paramTournamentCode } = useParams();
  const tournamentCode = propTournamentCode || paramTournamentCode;

  // Helper functions for image URLs
  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('blob:')) return logo;
    if (logo.startsWith('data:')) return logo;
    
    // For relative paths, prepend API_BASE_URL
    let path = logo;
    if (path.startsWith('/')) {
      path = path.slice(1); // Remove leading slash
    }
    if (path.startsWith('uploads')) {
      return `${API_BASE_URL}/${path}`;
    }
    return `${API_BASE_URL}/${path}`;
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    if (photo.startsWith('blob:')) return photo;
    if (photo.startsWith('data:')) return photo;
    
    // For relative paths, prepend API_BASE_URL
    let path = photo;
    if (path.startsWith('/')) {
      path = path.slice(1); // Remove leading slash
    }
    if (path.startsWith('uploads')) {
      return `${API_BASE_URL}/${path}`;
    }
    return `${API_BASE_URL}/uploads/${path}`;
  };

  const [tournament, setTournament] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', city: '', role: '', remarks: '' });
  const [kitInfo, setKitInfo] = useState({ jerseyName: '', jerseyNumber: '', jerseySize: '', pantSize: '', jerseySleeveType: 'half' });
  const [countryCode, setCountryCode] = useState('+91');
  const [confirmed, setConfirmed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [, setPlayerId] = useState('');
  const [, setCardUrl] = useState('');
  const [photo, setPhoto] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [registeredPlayer, setRegisteredPlayer] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [realTimeErrors, setRealTimeErrors] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [limitModal, setLimitModal] = useState(null);
  const [, setRegistrationStats] = useState({ count: null, limit: null });
  const [, setStatsLoading] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [isPhotoUploaded, setIsPhotoUploaded] = useState(false);

  const steps = ['Player Details', 'Review & Submit'];
  const stepsTotal = steps.length;

  const receiptInputRef = useRef(null);
  const receiptDropRef = useRef(null);

  const handleJoinWhatsApp = () => {
    if (tournament?.whatsappGroupLink) {
      window.open(tournament.whatsappGroupLink, '_blank');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    const tournamentName = tournament?.name || 'Tournament';
    const shareText = `Register for ${tournamentName}\n\n${url}`;
    const encodedText = encodeURIComponent(shareText);
    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const fetchTournament = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tournaments/${tournamentCode}`);
      setTournament(res.data.tournament);
    } catch (err) {
      console.error(err);
      setMessage('❌ Tournament not found.');
    }
  }, [tournamentCode]);

  const fetchRegistrationStats = useCallback(async () => {
    if (!tournamentCode) return;
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/players/${tournamentCode}/count`);
      setRegistrationStats({
        count: typeof res.data.count === 'number' ? res.data.count : null,
        limit: typeof res.data.limit === 'number' ? res.data.limit : null
      });
    } catch (error) {
      console.error('Failed to load registration stats', error);
      setRegistrationStats((prev) => ({ ...prev, count: null }));
    } finally {
      setStatsLoading(false);
    }
  }, [tournamentCode]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  useEffect(() => {
    fetchRegistrationStats();
  }, [fetchRegistrationStats]);

  // Ensure scrolling works on this page
  useEffect(() => {
    // Add class to body/html to enable scrolling
    document.body.classList.add('player-register-page-active');
    document.documentElement.classList.add('player-register-page-active');
    
    // Ensure body/html can scroll
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflowY = 'auto';
    document.documentElement.style.height = 'auto';
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('player-register-page-active');
      document.documentElement.classList.remove('player-register-page-active');
      document.body.style.overflowY = '';
      document.body.style.height = '';
      document.documentElement.style.overflowY = '';
      document.documentElement.style.height = '';
    };
  }, []);

  useEffect(() => {
    if (adminMode) {
      setConfirmed(true);
    }
  }, [adminMode]);

  const getRegistrationStatus = () => {
    if (!tournament) return 'Loading';

    const now = new Date();
    const start = tournament.registrationStartDate ? new Date(tournament.registrationStartDate) : null;
    const end = tournament.registrationEndDate ? new Date(tournament.registrationEndDate) : null;

    if (tournament.registrationStatus === 'Closed Early') {
      return 'Closed Early';
    }

    if (tournament.playerRegistrationEnabled) {
      // Manual overrides should take precedence, even if dates are outside the window.
      if (tournament.registrationStatus === 'Closed') {
        return 'Closed';
      }
      return 'Active';
    }

    if (tournament.registrationStatus === 'Closed') {
      return 'Closed';
    }

    if (tournament.registrationStatus === 'Not Started') {
      return 'Not Started';
    }

    if (start && now < start) {
      return 'Not Started';
    }

    if (end && now > end) {
      return 'Closed';
    }

    return 'Closed';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['jerseyName', 'jerseyNumber', 'jerseySize', 'pantSize', 'jerseySleeveType'].includes(name)) {
      let processedValue = value;
      // Convert jersey name and jersey number to uppercase
      if (name === 'jerseyName' || name === 'jerseyNumber') {
        processedValue = value.toUpperCase();
      }
      // Only strip non-numeric characters for jersey size and pant size, not jersey number
      if (name === 'jerseySize' || name === 'pantSize') {
        processedValue = value.replace(/\D/g, '');
      }
      setKitInfo((prev) => ({ ...prev, [name]: processedValue }));
      validateField(name, processedValue);
    } else {
      const processedValue = (name === 'name' || name === 'city') ? value.toUpperCase() : value;
      setForm((prev) => ({ ...prev, [name]: processedValue }));
      validateField(name, processedValue);
    }
  };

  const validateField = (name, value) => {
    const errors = { ...realTimeErrors };

    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Name is required';
        } else if (value.length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else {
          delete errors.name;
        }
        break;
      case 'mobile': {
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          errors.mobile = 'Enter valid 10-digit mobile number';
        } else {
          delete errors.mobile;
        }
        break;
      }
      case 'city':
        if (!value.trim()) {
          errors.city = 'City/Place is required';
        } else {
          delete errors.city;
        }
        break;
      case 'role':
        if (!value) {
          errors.role = 'Please select a role';
        } else {
          delete errors.role;
        }
        break;
      case 'jerseyName':
        if (tournament?.kitInformationEnabled) {
          if (!value.trim()) {
            errors.jerseyName = 'Jersey name is required';
          } else if (value.trim().length > 50) {
            errors.jerseyName = 'Jersey name must be 50 characters or less';
          } else {
            delete errors.jerseyName;
          }
        }
        break;
      case 'jerseyNumber':
        if (tournament?.kitInformationEnabled) {
          if (!value || !value.trim()) {
            errors.jerseyNumber = 'Jersey number is required';
          } else if (value.trim().length > 20) {
            errors.jerseyNumber = 'Jersey number must be 20 characters or less';
          } else {
            delete errors.jerseyNumber;
          }
        }
        break;
      case 'jerseySize':
        if (tournament?.kitInformationEnabled) {
          const num = parseFloat(value);
          if (!value || isNaN(num) || num < 28 || num > 60) {
            errors.jerseySize = 'Jersey size must be between 28 and 60';
          } else {
            delete errors.jerseySize;
          }
        }
        break;
      case 'pantSize':
        if (tournament?.kitInformationEnabled && tournament?.kitPantSizeEnabled !== false) {
          const num = parseFloat(value);
          if (!value || isNaN(num) || num < 28 || num > 60) {
            errors.pantSize = 'Pant size must be between 28 and 60';
          } else {
            delete errors.pantSize;
          }
        } else {
          delete errors.pantSize;
        }
        break;
      case 'jerseySleeveType':
        if (tournament?.kitInformationEnabled) {
          if (!value || !['full', 'half'].includes(value)) {
            errors.jerseySleeveType = 'Please select sleeve type';
          } else {
            delete errors.jerseySleeveType;
          }
        }
        break;
      default:
        break;
    }

    setRealTimeErrors(errors);
  };

  const validateMobile = (mobile) => /^\d{10}$/.test(mobile);

  const validateForm = () => {
    const errors = {};

    if (!form.name.trim()) errors.name = 'Name is required';
    if (!validateMobile(form.mobile)) errors.mobile = 'Enter valid 10-digit mobile number';
    if (!form.city.trim()) errors.city = 'City/Place is required';
    if (!form.role) errors.role = 'Please select a role';
    if (!photo) errors.photo = 'Please upload a player photo';
    if (tournament?.paymentReceiptMandatory && !receipt) {
      errors.receipt = 'Payment receipt is required for this tournament';
    }

    // Validate kit information if enabled
    if (tournament?.kitInformationEnabled) {
      if (!kitInfo.jerseyName.trim()) {
        errors.jerseyName = 'Jersey name is required';
      } else if (kitInfo.jerseyName.trim().length > 50) {
        errors.jerseyName = 'Jersey name must be 50 characters or less';
      }
      if (!kitInfo.jerseyNumber || !kitInfo.jerseyNumber.trim()) {
        errors.jerseyNumber = 'Jersey number is required';
      } else if (kitInfo.jerseyNumber.trim().length > 20) {
        errors.jerseyNumber = 'Jersey number must be 20 characters or less';
      }
      const jerseySz = parseFloat(kitInfo.jerseySize);
      if (!kitInfo.jerseySize || isNaN(jerseySz) || jerseySz < 28 || jerseySz > 60) {
        errors.jerseySize = 'Jersey size must be between 28 and 60';
      }
      if (tournament?.kitPantSizeEnabled !== false) {
        const pantSz = parseFloat(kitInfo.pantSize);
        if (!kitInfo.pantSize || isNaN(pantSz) || pantSz < 28 || pantSz > 60) {
          errors.pantSize = 'Pant size must be between 28 and 60';
        }
      }
      if (!kitInfo.jerseySleeveType || !['full', 'half'].includes(kitInfo.jerseySleeveType)) {
        errors.jerseySleeveType = 'Please select sleeve type';
      }
    }

    setRealTimeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage('❌ File size exceeds 2MB limit.');
        return;
      }
      setReceipt(file);
      setReceiptPreview(file.name);
    }
  };

  const handleReceiptDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage('❌ File size exceeds 2MB limit.');
        return;
      }
      if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
        setMessage('❌ Only image files and PDFs are allowed.');
        return;
      }
      setReceipt(file);
      setReceiptPreview(file.name);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Generate Google Pay/UPI payment link
  const generateGooglePayLink = () => {
    if (!tournament) return null;

    // If custom link is provided, use it
    if (tournament.googlePayLink) {
      return tournament.googlePayLink;
    }

    // Generate UPI link if UPI ID is available
    if (tournament.googlePayUpiId) {
      const amount = tournament.googlePayAmount || '';
      const tournamentName = encodeURIComponent(tournament.name || 'Tournament Payment');
      const upiLink = `upi://pay?pa=${encodeURIComponent(tournament.googlePayUpiId)}&am=${amount}&cu=INR&tn=${tournamentName}`;
      return upiLink;
    }

    // Fallback to phone number link
    if (tournament.googlePayNumber) {
      const phoneNumber = tournament.googlePayNumber.replace(/\D/g, ''); // Remove non-digits
      return `tel:+91${phoneNumber}`;
    }

    return null;
  };

  // Handle Google Pay link click
  const handleGooglePayClick = () => {
    const paymentLink = generateGooglePayLink();
    if (!paymentLink) {
      setMessage('❌ Google Pay link is not configured.');
      return;
    }

    // Try to open UPI link (will open Google Pay app if installed)
    if (paymentLink.startsWith('upi://')) {
      window.location.href = paymentLink;
    } else if (paymentLink.startsWith('tel:')) {
      window.location.href = paymentLink;
    } else {
      // For custom links, open in new tab
      window.open(paymentLink, '_blank');
    }
  };

  // Check if Google Pay should be displayed
  const shouldShowGooglePay = () => {
    if (!tournament) return false;
    if (!tournament.googlePayEnabled) return false;
    
    // Check if at least one payment method is configured
    return !!(tournament.googlePayLink || tournament.googlePayUpiId || tournament.googlePayNumber);
  };

  const getRoleOptions = () => {
    if (!tournament) return [];
    if (tournament.sport === 'Cricket') return ['Batsman', 'Bowler', 'All-Rounder', 'WK Batsman'];
    if (tournament.sport === 'Football') return ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
    if (tournament.sport === 'Volleyball') return ['Setter', 'Attacker', 'Blocker', 'Libero'];
    if (tournament.sport === 'Basketball') return ['Point Guard', 'Center', 'Forward', 'Shooting Guard'];
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (activeStep !== stepsTotal) {
      if (validateStep(activeStep)) {
        setActiveStep(stepsTotal);
      }
      return;
    }

    if (!validateForm()) return;
    if (!confirmed) {
      setMessage('❌ Please confirm that the details are correct.');
      return;
    }

    setIsSubmitting(true);
    setShowUploadProgress(true);
    setUploadProgress(0);

    try {
      const data = new FormData();
      Object.keys(form).forEach((key) => data.append(key, form[key]));
      data.append('tournamentCode', tournamentCode);
      data.append('countryCode', countryCode);
      if (photo) data.append('photo', photo);
      if (receipt) data.append('receipt', receipt);
      // Add kit information if enabled
      if (tournament?.kitInformationEnabled) {
        data.append('jerseyName', kitInfo.jerseyName.trim());
        data.append('jerseyNumber', kitInfo.jerseyNumber);
        data.append('jerseySize', kitInfo.jerseySize);
        if (tournament?.kitPantSizeEnabled !== false) {
          data.append('pantSize', kitInfo.pantSize);
        }
        data.append('jerseySleeveType', kitInfo.jerseySleeveType);
      }

      const res = await axios.post(`${API_BASE_URL}/api/players/register`, data, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setPlayerId(res.data.player.playerId);
      setCardUrl(res.data.cardUrl || '');
      setRegisteredPlayer(res.data.player);
      fetchRegistrationStats();
      
      // Update message based on registration status
      const registrationStatus = res.data.registrationStatus || res.data.player.registrationStatus || 'confirmed';
      if (registrationStatus === 'pending') {
        setMessage('✅ Registration submitted! Your registration is pending admin approval.');
      } else {
        setMessage('✅ Registration successful! You are confirmed for the tournament.');
      }
      
      if (adminMode && onSuccess) {
        onSuccess();
      } else {
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error(err);
      const apiMessage = err?.response?.data?.message;
      setMessage(apiMessage ? `❌ ${apiMessage}` : '❌ Error registering player. Please try again.');
      if (err?.response?.data?.code === 'PLAYER_LIMIT_REACHED') {
        fetchTournament();
        fetchRegistrationStats();
        setLimitModal({
          title: 'Registration Closed',
          message: apiMessage || 'Player registration is closed because the limit has been reached.'
        });
      }
      if (err?.response?.data?.code === 'RECEIPT_REQUIRED') {
        setRealTimeErrors((prev) => ({ ...prev, receipt: apiMessage || 'Payment receipt is required for this tournament' }));
        setActiveStep(1); // Go back to step 1 to show the error
      }
    } finally {
      setIsSubmitting(false);
      setShowUploadProgress(false);
      setUploadProgress(0);
    }
  };

  const resetForm = useCallback(() => {
    setForm({ name: '', mobile: '', city: '', role: '', remarks: '' });
    setKitInfo({ jerseyName: '', jerseyNumber: '', jerseySize: '', pantSize: '', jerseySleeveType: 'half' });
    setCountryCode('+91');
    setConfirmed(false);
    setPhoto(null);
    setIsPhotoUploaded(false);
    setReceipt(null);
    setReceiptPreview(null);
    setMessage('');
    setRealTimeErrors({});
    setActiveStep(1);
  }, []);

  const status = getRegistrationStatus();

  const renderStatusCallout = () => {
    if (status === 'Active') return null;

    const isUpcoming = status === 'Not Started';
    const title = isUpcoming ? 'Registration not open yet' : 'Registration closed';
    const description = isUpcoming
      ? `Registration opens on ${new Date(tournament.registrationStartDate).toLocaleDateString()}. Please check back soon.`
      : status === 'Closed'
        ? `Player registration closed on ${new Date(tournament.registrationEndDate).toLocaleDateString()}.`
        : 'Registration was closed early by the tournament admin.';

    return (
      <section className={`status-callout ${isUpcoming ? 'upcoming' : 'closed'}`}>
        <div className="callout-icon">{isUpcoming ? '🕒' : '🚫'}</div>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </section>
    );
  };

  const validateStep = useCallback((step) => {
    const draftErrors = { ...realTimeErrors };

    if (step === 1) {
      delete draftErrors.name;
      delete draftErrors.role;
      delete draftErrors.mobile;
      delete draftErrors.city;
      delete draftErrors.photo;
      delete draftErrors.receipt;
      delete draftErrors.jerseyName;
      delete draftErrors.jerseyNumber;
      delete draftErrors.jerseySize;
      delete draftErrors.pantSize;
      delete draftErrors.jerseySleeveType;

      if (!form.name.trim()) {
        draftErrors.name = 'Name is required';
      }
      if (!form.role) {
        draftErrors.role = 'Please select a role';
      }
      if (!validateMobile(form.mobile)) {
        draftErrors.mobile = 'Enter valid 10-digit mobile number';
      }
      if (!form.city.trim()) {
        draftErrors.city = 'City/Place is required';
      }
      if (!photo) {
        draftErrors.photo = 'Please upload a player photo';
      }
      if (tournament?.paymentReceiptMandatory && !receipt) {
        draftErrors.receipt = 'Payment receipt is required for this tournament';
      }

      // Validate kit information if enabled
      if (tournament?.kitInformationEnabled) {
        if (!kitInfo.jerseyName.trim()) {
          draftErrors.jerseyName = 'Jersey name is required';
        } else if (kitInfo.jerseyName.trim().length > 50) {
          draftErrors.jerseyName = 'Jersey name must be 50 characters or less';
        }
        if (!kitInfo.jerseyNumber || !kitInfo.jerseyNumber.trim()) {
          draftErrors.jerseyNumber = 'Jersey number is required';
        } else if (kitInfo.jerseyNumber.trim().length > 20) {
          draftErrors.jerseyNumber = 'Jersey number must be 20 characters or less';
        }
        const jerseySz = parseFloat(kitInfo.jerseySize);
        if (!kitInfo.jerseySize || isNaN(jerseySz) || jerseySz < 28 || jerseySz > 60) {
          draftErrors.jerseySize = 'Jersey size must be between 28 and 60';
        }
        if (tournament?.kitPantSizeEnabled !== false) {
          const pantSz = parseFloat(kitInfo.pantSize);
          if (!kitInfo.pantSize || isNaN(pantSz) || pantSz < 28 || pantSz > 60) {
            draftErrors.pantSize = 'Pant size must be between 28 and 60';
          }
        }
        if (!kitInfo.jerseySleeveType || !['full', 'half'].includes(kitInfo.jerseySleeveType)) {
          draftErrors.jerseySleeveType = 'Please select sleeve type';
        }
      }
    }

    setRealTimeErrors(draftErrors);

    if (step === 1) {
      const baseErrors = !draftErrors.name && !draftErrors.role && !draftErrors.mobile && !draftErrors.city && !draftErrors.photo && !draftErrors.receipt;
      if (!tournament?.kitInformationEnabled) {
        return baseErrors;
      }
      return baseErrors && !draftErrors.jerseyName && !draftErrors.jerseyNumber && !draftErrors.jerseySize && !draftErrors.pantSize && !draftErrors.jerseySleeveType;
    }
    return true;
  }, [form, photo, receipt, kitInfo, tournament, realTimeErrors]);

  const handleNextStep = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, stepsTotal));
    }
  }, [activeStep, validateStep, stepsTotal]);

  const handlePrevStep = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 1));
  }, []);

  if (!tournament) {
    return (
      <div className="player-register-page">
        <div className="register-loading-card">
          <div className="loading-spinner"></div>
          <p>Loading tournament details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="player-register-page">
      {status !== 'Active' ? (
        <div className="register-container-simple">
          {renderStatusCallout()}
        </div>
      ) : (
        <div className="register-container-simple">
          <div className="register-form-simple">
            <form onSubmit={handleSubmit} noValidate>
              {activeStep === 1 && (
                <div className="form-section-simple">
                  <div className="tournament-header-inline">
                    {tournament.logo && (
                      <img
                        src={getLogoUrl(tournament.logo)}
                        alt={`${tournament.name} logo`}
                        className="tournament-logo-inline"
                        onClick={() => setShowLogoModal(true)}
                        style={{ cursor: 'pointer' }}
                        title="Click to view larger logo"
                      />
                    )}
                    <h1 className="tournament-name-inline">{tournament.name}</h1>
                    <div className="registration-status-badge">
                      <div className={`status-indicator ${status === 'Active' ? 'status-open' : 'status-closed'}`}>
                        {status === 'Active' ? 'Open' : 'Closed'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="share-button whatsapp-share-button"
                      onClick={handleShare}
                      title="Share on WhatsApp"
                      aria-label="Share registration page on WhatsApp"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>
                      </svg>
                      <span>Share on WhatsApp</span>
                    </button>
                  </div>
                  <h2>Player Registration</h2>
                  {tournament?.registrationInstructions && tournament.registrationInstructions.trim() && (
                    <div 
                      className="registration-instructions-box"
                      style={{
                        backgroundColor: '#e0f2fe',
                        border: '1px solid #0284c7',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '24px',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div 
                        style={{
                          flex: 1,
                          color: '#0c4a6e',
                          fontSize: '15px',
                          lineHeight: '1.6',
                          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}
                        dangerouslySetInnerHTML={{ __html: tournament.registrationInstructions }}
                      />
                    </div>
                  )}
                  <div className="form-grid-simple">
                    <div className={`form-field ${realTimeErrors.name ? 'has-error' : ''}`}>
                      <label htmlFor="player-name">Full Name *</label>
                      <input
                        id="player-name"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        required
                      />
                      {realTimeErrors.name && <span className="field-error">{realTimeErrors.name}</span>}
                    </div>
                    <div className={`form-field ${realTimeErrors.role ? 'has-error' : ''}`}>
                      <label htmlFor="role">Playing Role *</label>
                      <select
                        id="role"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select role</option>
                        {getRoleOptions().map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      {realTimeErrors.role && <span className="field-error">{realTimeErrors.role}</span>}
                    </div>
                    <div className={`form-field ${realTimeErrors.mobile ? 'has-error' : ''}`}>
                      <label htmlFor="mobile-number">Mobile Number *</label>
                      <div className="phone-input">
                        <select
                          id="country-code"
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                        >
                          <option value="+91">+91</option>
                          <option value="+966">+966</option>
                        </select>
                        <input
                          id="mobile-number"
                          type="tel"
                          name="mobile"
                          value={form.mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            handleChange({ target: { name: 'mobile', value } });
                          }}
                          placeholder="9876543210"
                          maxLength="10"
                          required
                        />
                      </div>
                      {realTimeErrors.mobile && <span className="field-error">{realTimeErrors.mobile}</span>}
                    </div>
                    <div className={`form-field ${realTimeErrors.city ? 'has-error' : ''}`}>
                      <label htmlFor="city">City / Hometown *</label>
                      <input
                        id="city"
                        type="text"
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="Enter your city"
                        required
                      />
                      {realTimeErrors.city && <span className="field-error">{realTimeErrors.city}</span>}
                    </div>
                    <div className="form-field full-width">
                      <label htmlFor="remarks">Highlights (Optional)</label>
                      <textarea
                        id="remarks"
                        name="remarks"
                        value={form.remarks}
                        onChange={handleChange}
                        placeholder="Share your achievements, experience, etc."
                        rows="3"
                      />
                    </div>
                    <div className={`form-field full-width photo-uploader ${realTimeErrors.photo ? 'has-error' : ''}`}>
                      <label>Player Photo *</label>
                      <ImageUploadCrop
                        uploadType="playerPhoto"
                        aspect={1}
                        uploadPath={`${API_BASE_URL}/api/players/upload-photo`}
                        placeholder="Upload your photo"
                        maxSizeMB={2}
                        onUploadStart={() => {
                          setRealTimeErrors((prev) => {
                            const next = { ...prev };
                            delete next.photo;
                            return next;
                          });
                        }}
                        onUploadEnd={() => {}}
                        onComplete={(url) => {
                          setPhoto(url);
                          setIsPhotoUploaded(true);
                          setRealTimeErrors((prev) => {
                            const next = { ...prev };
                            delete next.photo;
                            return next;
                          });
                        }}
                        onError={(errorMessage) => {
                          setPhoto(null);
                          setIsPhotoUploaded(false);
                          setRealTimeErrors((prev) => ({ ...prev, photo: errorMessage }));
                        }}
                      />
                      {isPhotoUploaded && photo && (
                        <span className="image-upload-success">✓ Image uploaded</span>
                      )}
                      {realTimeErrors.photo && <span className="field-error">{realTimeErrors.photo}</span>}
                    </div>
                    {shouldShowGooglePay() && (
                      <div className="form-field full-width">
                        <label>Payment via Google Pay</label>
                        <button
                          type="button"
                          onClick={handleGooglePayClick}
                          className="google-pay-button"
                          style={{
                            width: '100%',
                            padding: '14px 20px',
                            backgroundColor: '#4285f4',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            transition: 'background-color 0.2s',
                            marginBottom: '8px'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#357ae8'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#4285f4'}
                        >
                          <span style={{ fontSize: '20px' }}>💳</span>
                          <span>
                            Pay via Google Pay
                            {tournament?.googlePayAmount && ` - ₹${tournament.googlePayAmount}`}
                          </span>
                        </button>
                        <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                          Click to open Google Pay and complete payment
                        </small>
                      </div>
                    )}
                    <div className={`form-field full-width ${realTimeErrors.receipt ? 'has-error' : ''}`}>
                      <label htmlFor="receipt-upload">
                        Payment Receipt {tournament?.paymentReceiptMandatory ? '(Required)' : '(Optional)'}
                      </label>
                      <div
                        ref={receiptDropRef}
                        className="receipt-dropzone"
                        onClick={() => receiptInputRef.current?.click()}
                        onDrop={handleReceiptDrop}
                        onDragOver={handleDragOver}
                        role="button"
                        tabIndex={0}
                      >
                        {receiptPreview ? (
                          <div className="dropzone-preview">
                            <span className="file-icon">📄</span>
                            <div>
                              <strong>{receiptPreview}</strong>
                              <small>Click to change</small>
                            </div>
                          </div>
                        ) : (
                          <div className="dropzone-placeholder">
                            <span className="upload-icon">📤</span>
                            <div>
                              <p>Drag & drop or click to upload</p>
                              <small>JPG or PDF up to 2MB</small>
                            </div>
                          </div>
                        )}
                      </div>
                      <input
                        ref={receiptInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          handleReceiptChange(e);
                          if (e.target.files[0]) {
                            setRealTimeErrors((prev) => {
                              const next = { ...prev };
                              delete next.receipt;
                              return next;
                            });
                          }
                        }}
                        required={tournament?.paymentReceiptMandatory || false}
                        style={{ display: 'none' }}
                      />
                      {realTimeErrors.receipt && <span className="field-error">{realTimeErrors.receipt}</span>}
                    </div>
                    {tournament?.kitInformationEnabled && (
                      <>
                        <div className="form-field full-width" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: 'rgba(148, 163, 184, 0.9)' }}>Kit Information *</h3>
                        </div>
                        <div className={`form-field ${realTimeErrors.jerseyName ? 'has-error' : ''}`}>
                          <label htmlFor="jersey-name">Jersey Name *</label>
                          <input
                            id="jersey-name"
                            type="text"
                            name="jerseyName"
                            value={kitInfo.jerseyName}
                            onChange={handleChange}
                            placeholder="Enter name for jersey"
                            maxLength="50"
                            required
                          />
                          {realTimeErrors.jerseyName && <span className="field-error">{realTimeErrors.jerseyName}</span>}
                        </div>
                        <div className={`form-field ${realTimeErrors.jerseyNumber ? 'has-error' : ''}`}>
                          <label htmlFor="jersey-number">Jersey Number *</label>
                          <input
                            id="jersey-number"
                            type="text"
                            name="jerseyNumber"
                            value={kitInfo.jerseyNumber}
                            onChange={handleChange}
                            placeholder="e.g., R006, B007, 10"
                            maxLength="20"
                            required
                          />
                          {realTimeErrors.jerseyNumber && <span className="field-error">{realTimeErrors.jerseyNumber}</span>}
                        </div>
                        <div className={`form-field ${realTimeErrors.jerseySize ? 'has-error' : ''}`}>
                          <label htmlFor="jersey-size">Jersey Size *</label>
                          <input
                            id="jersey-size"
                            type="number"
                            name="jerseySize"
                            value={kitInfo.jerseySize}
                            onChange={handleChange}
                            placeholder="Enter jersey size (28-60)"
                            min="28"
                            max="60"
                            step="1"
                            required
                          />
                          {realTimeErrors.jerseySize && <span className="field-error">{realTimeErrors.jerseySize}</span>}
                        </div>
                        {tournament?.kitPantSizeEnabled !== false && (
                          <div className={`form-field ${realTimeErrors.pantSize ? 'has-error' : ''}`}>
                            <label htmlFor="pant-size">Pant Size *</label>
                            <input
                              id="pant-size"
                              type="number"
                              name="pantSize"
                              value={kitInfo.pantSize}
                              onChange={handleChange}
                              placeholder="Enter pant size (28-60)"
                              min="28"
                              max="60"
                              step="1"
                              required
                            />
                            {realTimeErrors.pantSize && <span className="field-error">{realTimeErrors.pantSize}</span>}
                          </div>
                        )}
                        <div className={`form-field ${realTimeErrors.jerseySleeveType ? 'has-error' : ''}`}>
                          <label htmlFor="jersey-sleeve-type">Jersey Sleeve Type *</label>
                          <select
                            id="jersey-sleeve-type"
                            name="jerseySleeveType"
                            value={kitInfo.jerseySleeveType}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select sleeve type</option>
                            <option value="half">Half Sleeve</option>
                            <option value="full">Full Sleeve</option>
                          </select>
                          {realTimeErrors.jerseySleeveType && <span className="field-error">{realTimeErrors.jerseySleeveType}</span>}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="form-actions-simple">
                    <button type="button" className="btn-primary" onClick={handleNextStep}>
                      Review & Submit →
                    </button>
                  </div>
                </div>
              )}

              {activeStep === 2 && (
                <div className="form-section-simple">
                  <div className="tournament-header-inline">
                    {tournament.logo && (
                      <img
                        src={getLogoUrl(tournament.logo)}
                        alt={`${tournament.name} logo`}
                        className="tournament-logo-inline"
                        onClick={() => setShowLogoModal(true)}
                        style={{ cursor: 'pointer' }}
                        title="Click to view larger logo"
                      />
                    )}
                    <h1 className="tournament-name-inline">{tournament.name}</h1>
                    <div className="registration-status-badge">
                      <div className={`status-indicator ${status === 'Active' ? 'status-open' : 'status-closed'}`}>
                        {status === 'Active' ? 'Open' : 'Closed'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="share-button whatsapp-share-button"
                      onClick={handleShare}
                      title="Share on WhatsApp"
                      aria-label="Share registration page on WhatsApp"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>
                      </svg>
                      <span>Share on WhatsApp</span>
                    </button>
                  </div>
                  <h2>Review Your Information</h2>
                  <div className="review-summary">
                    <div className="review-item">
                      <span>Name:</span>
                      <strong>{form.name || 'Not provided'}</strong>
                    </div>
                    <div className="review-item">
                      <span>Role:</span>
                      <strong>{form.role || 'Not selected'}</strong>
                    </div>
                    <div className="review-item">
                      <span>Mobile:</span>
                      <strong>{form.mobile ? `${countryCode} ${form.mobile}` : 'Not provided'}</strong>
                    </div>
                    <div className="review-item">
                      <span>City:</span>
                      <strong>{form.city || 'Not provided'}</strong>
                    </div>
                    {form.remarks && (
                      <div className="review-item">
                        <span>Highlights:</span>
                        <strong>{form.remarks}</strong>
                      </div>
                    )}
                    <div className="review-item">
                      <span>Photo:</span>
                      <strong>{photo ? '✓ Uploaded' : 'Not uploaded'}</strong>
                    </div>
                    <div className="review-item">
                      <span>Payment Receipt:</span>
                      <strong>{receipt ? '✓ Uploaded' : tournament?.paymentReceiptMandatory ? '⚠️ Required' : 'Not uploaded'}</strong>
                    </div>
                    {tournament?.kitInformationEnabled && (
                      <>
                        <div className="review-item" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                          <span style={{ fontWeight: '600', fontSize: '16px' }}>Kit Information:</span>
                        </div>
                        <div className="review-item">
                          <span>Jersey Name:</span>
                          <strong>{kitInfo.jerseyName || 'Not provided'}</strong>
                        </div>
                        <div className="review-item">
                          <span>Jersey Number:</span>
                          <strong>{kitInfo.jerseyNumber || 'Not provided'}</strong>
                        </div>
                        <div className="review-item">
                          <span>Jersey Size:</span>
                          <strong>{kitInfo.jerseySize || 'Not provided'}</strong>
                        </div>
                        {tournament?.kitPantSizeEnabled !== false && (
                          <div className="review-item">
                            <span>Pant Size:</span>
                            <strong>{kitInfo.pantSize || 'Not provided'}</strong>
                          </div>
                        )}
                        <div className="review-item">
                          <span>Jersey Sleeve Type:</span>
                          <strong>{kitInfo.jerseySleeveType === 'full' ? 'Full Sleeve' : kitInfo.jerseySleeveType === 'half' ? 'Half Sleeve' : 'Not provided'}</strong>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="confirmation-checkbox">
                    <label htmlFor="confirm-details">
                      <input
                        id="confirm-details"
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                      />
                      I confirm that all information is correct
                    </label>
                  </div>
                  {message && (
                    <div className={`message ${message.includes('❌') ? 'error' : 'success'}`} role="alert">
                      <p>{message}</p>
                    </div>
                  )}
                  <div className="form-actions-simple">
                    <button type="button" className="btn-secondary" onClick={handlePrevStep}>
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isSubmitting || !confirmed || Object.keys(realTimeErrors).length > 0}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-small"></span>
                          Submitting...
                        </>
                      ) : (
                        'Submit Registration'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {showUploadProgress && (
        <div className="progress-overlay">
          <div className="progress-card">
            <div className="progress-header">
              <div className="progress-spinner"></div>
              <div className="progress-text">
                <div className="progress-percentage">{uploadProgress}%</div>
                <div className="progress-message">Uploading player registration…</div>
              </div>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <div className="progress-subtext">
              Keep this window open until the upload completes.
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div
          className="success-modal-overlay"
          onClick={() => {
            setShowSuccessModal(false);
            resetForm();
          }}
        >
          <div
            className="success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-success-title"
            aria-describedby="registration-success-description"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-glow" aria-hidden="true"></div>
            <div className="confetti-splash" aria-hidden="true"></div>
            <header className="success-header">
              <div className="success-header__top">
                {tournament?.logo && (
                  <div className="success-tournament-logo-wrapper">
                    <img
                      src={getLogoUrl(tournament.logo)}
                      alt={`${tournament.name} logo`}
                      className="success-tournament-logo"
                      onClick={() => setShowLogoModal(true)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view larger logo"
                    />
                  </div>
                )}
                <div className="success-header__info">
                  <h1 className="success-tournament-name">{tournament?.name || 'Tournament'}</h1>
                  <p className="success-tournament-label">
                    {(registeredPlayer?.registrationStatus || 'confirmed') === 'pending' 
                      ? 'Registration Submitted - Pending Approval'
                      : 'Player Registration Successful'}
                  </p>
                </div>
              </div>
            </header>

            <div className="success-player-details">
              <div className="success-player-image">
                {registeredPlayer?.photo ? (
                  <img
                    src={getPhotoUrl(registeredPlayer.photo)}
                    alt={registeredPlayer?.name || form.name}
                    className="player-photo-success"
                  />
                ) : photo ? (
                  <img
                    src={getPhotoUrl(photo)}
                    alt={form.name}
                    className="player-photo-success"
                  />
                ) : null}
              </div>
              <div className="success-player-info">
                <div className="success-info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{registeredPlayer?.name || form.name}</span>
                </div>
                <div className="success-info-item">
                  <span className="info-label">Role:</span>
                  <span className="info-value">{registeredPlayer?.role || form.role || 'N/A'}</span>
                </div>
                <div className="success-info-item">
                  <span className="info-label">Mobile:</span>
                  <span className="info-value">
                    {registeredPlayer?.mobile
                      ? `${registeredPlayer?.countryCode || '+91'} ${registeredPlayer.mobile}`
                      : form.mobile
                        ? `${countryCode} ${form.mobile}`
                        : 'N/A'}
                  </span>
                </div>
                <div className="success-info-item">
                  <span className="info-label">City:</span>
                  <span className="info-value">{registeredPlayer?.city || form.city || 'N/A'}</span>
                </div>
                {registeredPlayer?.remarks || form.remarks ? (
                  <div className="success-info-item">
                    <span className="info-label">Highlights:</span>
                    <span className="info-value">{registeredPlayer?.remarks || form.remarks}</span>
                  </div>
                ) : null}
                {(registeredPlayer?.registrationStatus || 'confirmed') === 'pending' && (
                  <div className="success-info-item" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                    <span style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: '500' }}>
                      ⏳ Your registration is pending admin approval. You will be notified once it's reviewed.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="success-actions">
              <div className="success-actions__primary">
                {tournament?.whatsappGroupLink && (
                  <button
                    type="button"
                    className="success-button whatsapp-button"
                    onClick={handleJoinWhatsApp}
                    style={{
                      backgroundColor: '#25D366',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>
                    </svg>
                    Join WhatsApp Group
                  </button>
                )}
                <button
                  type="button"
                  className="success-button register-another"
                  onClick={() => {
                    setShowSuccessModal(false);
                    resetForm();
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="https://www.w3.org/2000/svg">
                    <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Register another player
                </button>
                <button
                  type="button"
                  className="success-button close-button"
                  onClick={() => {
                    setShowSuccessModal(false);
                    resetForm();
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="https://www.w3.org/2000/svg">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {limitModal && (
        <div
          className="success-modal-overlay"
          onClick={() => setLimitModal(null)}
        >
          <div
            className="success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="limit-modal-title"
            aria-describedby="limit-modal-description"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="success-close"
              onClick={() => setLimitModal(null)}
              aria-label="Close registration limit notice"
            >
              ×
            </button>
            <header className="success-header">
              <div className="success-header__top">
                <div className="success-header__info">
                  <p className="success-tournament-label">NOTICE</p>
                  <h2 id="limit-modal-title">{limitModal.title}</h2>
                </div>
              </div>
            </header>
            <div className="success-message">
              <p id="limit-modal-description">{limitModal.message}</p>
            </div>
            <div className="success-actions">
              <button
                type="button"
                className="success-button primary"
                onClick={() => setLimitModal(null)}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoModal && tournament?.logo && (
        <div
          className="logo-modal-overlay"
          onClick={() => setShowLogoModal(false)}
        >
          <div
            className="logo-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="logo-modal-close"
              onClick={() => setShowLogoModal(false)}
              aria-label="Close logo modal"
            >
              ×
            </button>
            <img
              src={
                tournament.logo.startsWith('http')
                  ? tournament.logo
                  : (tournament.logo.startsWith('/') ? tournament.logo : `/${tournament.logo}`)
              }
              alt={`${tournament.name} logo`}
              className="logo-modal-image"
            />
            <p className="logo-modal-title">{tournament.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerRegister;
